import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const AccountsReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeReport, setActiveReport] = useState('gst-summary');

  // Report data states
  const [gstReport, setGstReport] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'list'

  // Date filters
  const [filters, setFilters] = useState({
    as_of_date: new Date().toISOString().split('T')[0],
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    pink: '#ec4899',
    orange: '#f97316',
    teal: '#14b8a6'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [activeReport, filters]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      switch (activeReport) {
        case 'gst-summary':
          const gstData = await apiGet(`/admin/accounts/reports/gst-summary?start_date=${filters.start_date}&end_date=${filters.end_date}`);
          if (gstData.success) {
            setGstReport(gstData.data);
            setTransactions(gstData.data.transactions || []);
          }
          break;

        case 'trial-balance':
          const tbData = await apiGet('/admin/accounts/reports/trial-balance?as_of_date=${filters.as_of_date}');
          if (tbData.success) setTrialBalance(tbData.data);
          break;
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    }
    setLoading(false);
  };

  // Export to Excel function
  const exportToExcel = async () => {
    try {
      const response = await fetch(
        `/admin/accounts/reports/gst-export?start_date=${filters.start_date}&end_date=${filters.end_date}`,
        { method: 'GET' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GST_Report_${filters.start_date}_to_${filters.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        alert('Error downloading report');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Error exporting to Excel');
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    headerRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: '16px',
      marginBottom: '16px'
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      flexWrap: 'wrap'
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.textDark,
      border: `2px solid ${active ? colors.primary : colors.mediumGray}`,
      borderRadius: '10px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }),
    filterCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '14px',
      marginBottom: isMobile ? '16px' : '20px',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 20px 0',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    reportTitle: {
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: '24px'
    },
    summaryCard: (bgColor, textColor) => ({
      background: `linear-gradient(135deg, ${bgColor}15, ${bgColor}25)`,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '14px',
      border: `1px solid ${bgColor}30`,
      position: 'relative',
      overflow: 'hidden'
    }),
    summaryIcon: {
      fontSize: isMobile ? '24px' : '32px',
      marginBottom: '8px'
    },
    summaryLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      fontWeight: '600',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    summaryValue: (color) => ({
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: '700',
      color: color
    }),
    summarySubtext: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      marginTop: '4px'
    },
    chartContainer: {
      marginTop: '24px',
      padding: isMobile ? '16px' : '24px',
      background: colors.lightGray,
      borderRadius: '14px'
    },
    barChart: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-around',
      height: isMobile ? '200px' : '280px',
      gap: isMobile ? '8px' : '16px',
      padding: '20px 0'
    },
    barItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1
    },
    bar: (height, color) => ({
      width: isMobile ? '40px' : '60px',
      height: `${Math.max(height, 5)}%`,
      background: `linear-gradient(180deg, ${color}, ${color}CC)`,
      borderRadius: '8px 8px 0 0',
      transition: 'height 0.5s ease',
      minHeight: '10px'
    }),
    barLabel: {
      fontSize: isMobile ? '10px' : '12px',
      color: colors.textDark,
      fontWeight: '600',
      marginTop: '8px',
      textAlign: 'center'
    },
    barValue: {
      fontSize: isMobile ? '9px' : '11px',
      color: colors.textLight,
      marginTop: '4px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '11px' : '13px'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '10px 8px' : '14px 12px',
      textAlign: 'left',
      fontWeight: '700',
      borderBottom: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '10px' : '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: colors.textDark
    },
    td: {
      padding: isMobile ? '10px 8px' : '14px 12px',
      borderBottom: `1px solid ${colors.lightGray}`,
      verticalAlign: 'middle'
    },
    badge: (type) => {
      const badgeColors = {
        'revenue': { bg: colors.success + '20', text: colors.success },
        'expense': { bg: colors.danger + '20', text: colors.danger },
        'receipt': { bg: colors.blue + '20', text: colors.blue },
        'payment': { bg: colors.warning + '20', text: colors.warning }
      };
      const style = badgeColors[type] || badgeColors.revenue;
      return {
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600',
        background: style.bg,
        color: style.text,
        display: 'inline-block'
      };
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '10px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    toggleContainer: {
      display: 'flex',
      background: colors.lightGray,
      borderRadius: '10px',
      padding: '4px',
      gap: '4px'
    },
    toggleButton: (active) => ({
      padding: '8px 16px',
      background: active ? colors.white : 'transparent',
      color: active ? colors.primary : colors.textLight,
      border: 'none',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: active ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
    }),
    gstBreakdownGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '20px',
      marginTop: '24px'
    },
    breakdownCard: {
      background: colors.lightGray,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '14px'
    },
    breakdownTitle: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    breakdownRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: `1px solid ${colors.mediumGray}`
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      fontSize: isMobile ? '13px' : '14px'
    },
    donutContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '16px'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px'
    },
    legendDot: (color) => ({
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: color
    })
  };

  // Simple Donut Chart Component
  const DonutChart = ({ data, size = 180 }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;

    const createArc = (startAngle, endAngle, color, index) => {
      const radius = size / 2 - 10;
      const innerRadius = radius * 0.6;
      const cx = size / 2;
      const cy = size / 2;

      const startX = cx + radius * Math.cos((startAngle - 90) * Math.PI / 180);
      const startY = cy + radius * Math.sin((startAngle - 90) * Math.PI / 180);
      const endX = cx + radius * Math.cos((endAngle - 90) * Math.PI / 180);
      const endY = cy + radius * Math.sin((endAngle - 90) * Math.PI / 180);

      const innerStartX = cx + innerRadius * Math.cos((endAngle - 90) * Math.PI / 180);
      const innerStartY = cy + innerRadius * Math.sin((endAngle - 90) * Math.PI / 180);
      const innerEndX = cx + innerRadius * Math.cos((startAngle - 90) * Math.PI / 180);
      const innerEndY = cy + innerRadius * Math.sin((startAngle - 90) * Math.PI / 180);

      const largeArc = endAngle - startAngle > 180 ? 1 : 0;

      const d = `
        M ${startX} ${startY}
        A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}
        L ${innerStartX} ${innerStartY}
        A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEndX} ${innerEndY}
        Z
      `;

      return <path key={index} d={d} fill={color} />;
    };

    return (
      <svg width={size} height={size}>
        {data.map((item, index) => {
          if (item.value === 0) return null;
          const angle = (item.value / total) * 360;
          const arc = createArc(currentAngle, currentAngle + angle, item.color, index);
          currentAngle += angle;
          return arc;
        })}
        <text x={size / 2} y={size / 2 - 10} textAnchor="middle" fontSize="14" fontWeight="700" fill={colors.textDark}>
          Total GST
        </text>
        <text x={size / 2} y={size / 2 + 15} textAnchor="middle" fontSize="18" fontWeight="700" fill={colors.primary}>
          ₹{total.toLocaleString('en-IN')}
        </text>
      </svg>
    );
  };

  // GST Summary Report Component
  const GSTSummaryReport = () => {
    if (!gstReport) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <div style={{ color: colors.textLight }}>Loading GST Report...</div>
        </div>
      );
    }

    const { summary, breakdown, transactions: txnList } = gstReport;

    // Calculate chart data
    const maxValue = Math.max(
      summary?.total_revenue || 0,
      summary?.total_expenses || 0,
      summary?.output_gst || 0,
      summary?.input_gst || 0
    );

    const chartData = [
      { label: 'Revenue', value: summary?.total_revenue || 0, color: colors.success },
      { label: 'Expenses', value: summary?.total_expenses || 0, color: colors.danger },
      { label: 'Output GST', value: summary?.output_gst || 0, color: colors.blue },
      { label: 'Input GST', value: summary?.input_gst || 0, color: colors.purple }
    ];

    const donutData = [
      { name: 'Room GST', value: breakdown?.room_gst || 0, color: colors.success },
      { name: 'F&B GST', value: breakdown?.fnb_gst || 0, color: colors.blue },
      { name: 'Bar GST', value: breakdown?.bar_gst || 0, color: colors.purple },
      { name: 'Other GST', value: breakdown?.other_gst || 0, color: colors.warning }
    ];

    return (
      <div>
        {/* Header with Export Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={styles.reportTitle}>
            <span>🧾</span> GST Summary Report
          </h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={styles.toggleContainer}>
              <button
                style={styles.toggleButton(viewMode === 'chart')}
                onClick={() => setViewMode('chart')}
              >
                📊 Charts
              </button>
              <button
                style={styles.toggleButton(viewMode === 'list')}
                onClick={() => setViewMode('list')}
              >
                📋 List
              </button>
            </div>
            <button style={styles.button} onClick={exportToExcel}>
              <span>📥</span> Export Excel
            </button>
          </div>
        </div>

        <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '20px' }}>
          Period: {new Date(filters.start_date).toLocaleDateString('en-IN')} to {new Date(filters.end_date).toLocaleDateString('en-IN')}
        </p>

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard(colors.success, colors.success)}>
            <div style={styles.summaryIcon}>💰</div>
            <div style={styles.summaryLabel}>Total Revenue</div>
            <div style={styles.summaryValue(colors.success)}>
              ₹{(summary?.total_revenue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={styles.summarySubtext}>
              GST: ₹{(summary?.output_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>

          <div style={styles.summaryCard(colors.danger, colors.danger)}>
            <div style={styles.summaryIcon}>💸</div>
            <div style={styles.summaryLabel}>Total Expenses</div>
            <div style={styles.summaryValue(colors.danger)}>
              ₹{(summary?.total_expenses || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={styles.summarySubtext}>
              GST: ₹{(summary?.input_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>

          <div style={styles.summaryCard(colors.blue, colors.blue)}>
            <div style={styles.summaryIcon}>📤</div>
            <div style={styles.summaryLabel}>Output GST</div>
            <div style={styles.summaryValue(colors.blue)}>
              ₹{(summary?.output_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={styles.summarySubtext}>
              CGST + SGST Collected
            </div>
          </div>

          <div style={styles.summaryCard(colors.purple, colors.purple)}>
            <div style={styles.summaryIcon}>📥</div>
            <div style={styles.summaryLabel}>Input GST</div>
            <div style={styles.summaryValue(colors.purple)}>
              ₹{(summary?.input_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={styles.summarySubtext}>
              GST Credit Available
            </div>
          </div>
        </div>

        {/* Net GST Payable Card */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.primary}10, ${colors.primary}20)`,
          padding: '20px',
          borderRadius: '14px',
          border: `2px solid ${colors.primary}40`,
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <div style={{ fontSize: '14px', color: colors.textLight, fontWeight: '600', marginBottom: '4px' }}>
              NET GST PAYABLE
            </div>
            <div style={{ fontSize: '12px', color: colors.textLight }}>
              Output GST - Input GST
            </div>
          </div>
          <div style={{
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: '700',
            color: (summary?.net_gst || 0) >= 0 ? colors.danger : colors.success
          }}>
            ₹{Math.abs(summary?.net_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            <span style={{ fontSize: '14px', fontWeight: '600', marginLeft: '8px' }}>
              {(summary?.net_gst || 0) >= 0 ? '(Payable)' : '(Refundable)'}
            </span>
          </div>
        </div>

        {viewMode === 'chart' ? (
          <>
            {/* Bar Chart */}
            <div style={styles.chartContainer}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: colors.textDark }}>
                📊 Financial Overview
              </h3>
              <div style={styles.barChart}>
                {chartData.map((item, index) => (
                  <div key={index} style={styles.barItem}>
                    <div style={styles.bar(maxValue > 0 ? (item.value / maxValue) * 100 : 0, item.color)} />
                    <div style={styles.barLabel}>{item.label}</div>
                    <div style={styles.barValue}>₹{item.value.toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* GST Breakdown */}
            <div style={styles.gstBreakdownGrid}>
              {/* Donut Chart */}
              <div style={styles.breakdownCard}>
                <div style={styles.breakdownTitle}>
                  <span>🥧</span> GST Distribution
                </div>
                <div style={styles.donutContainer}>
                  <DonutChart data={donutData} size={isMobile ? 160 : 200} />
                </div>
                <div style={styles.legend}>
                  {donutData.map((item, index) => (
                    <div key={index} style={styles.legendItem}>
                      <div style={styles.legendDot(item.color)} />
                      <span>{item.name}: ₹{item.value.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* GST Details */}
              <div style={styles.breakdownCard}>
                <div style={styles.breakdownTitle}>
                  <span>📋</span> GST Breakdown
                </div>

                <div style={styles.breakdownRow}>
                  <span style={{ fontWeight: '600' }}>Room Revenue GST (12%)</span>
                  <span style={{ color: colors.success, fontWeight: '600' }}>
                    ₹{(breakdown?.room_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ ...styles.breakdownRow, fontSize: '12px', color: colors.textLight }}>
                  <span>  ↳ CGST (6%)</span>
                  <span>₹{((breakdown?.room_gst || 0) / 2).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ ...styles.breakdownRow, fontSize: '12px', color: colors.textLight }}>
                  <span>  ↳ SGST (6%)</span>
                  <span>₹{((breakdown?.room_gst || 0) / 2).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>

                <div style={styles.breakdownRow}>
                  <span style={{ fontWeight: '600' }}>F&B GST (5%)</span>
                  <span style={{ color: colors.blue, fontWeight: '600' }}>
                    ₹{(breakdown?.fnb_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div style={styles.breakdownRow}>
                  <span style={{ fontWeight: '600' }}>Bar GST (18%)</span>
                  <span style={{ color: colors.purple, fontWeight: '600' }}>
                    ₹{(breakdown?.bar_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div style={styles.breakdownRow}>
                  <span style={{ fontWeight: '600' }}>Other Services GST</span>
                  <span style={{ color: colors.warning, fontWeight: '600' }}>
                    ₹{(breakdown?.other_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div style={{
                  ...styles.breakdownRow,
                  borderTop: `2px solid ${colors.mediumGray}`,
                  borderBottom: 'none',
                  paddingTop: '16px',
                  marginTop: '8px'
                }}>
                  <span style={{ fontWeight: '700', fontSize: '15px' }}>Total Output GST</span>
                  <span style={{ color: colors.primary, fontWeight: '700', fontSize: '15px' }}>
                    ₹{(summary?.output_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Transaction List View */
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: colors.textDark }}>
              📋 Transaction List ({txnList?.length || 0} transactions)
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Description</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Base Amount</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>GST</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {txnList && txnList.length > 0 ? txnList.map((txn, index) => (
                    <tr key={index}>
                      <td style={styles.td}>
                        {new Date(txn.date).toLocaleDateString('en-IN')}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(txn.type)}>
                          {txn.type}
                        </span>
                      </td>
                      <td style={styles.td}>{txn.description}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        ₹{parseFloat(txn.base_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', color: colors.blue }}>
                        ₹{parseFloat(txn.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                        ₹{parseFloat(txn.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>
                        No transactions found for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Trial Balance Report - FIXED with proper total calculation
  const TrialBalanceReport = () => {
    if (!trialBalance) return null;

    // Calculate totals from the account data
    let totalDebit = 0;
    let totalCredit = 0;

    const accountRows = trialBalance.accounts?.map(account => {
      const balance = parseFloat(account.closing_balance || account.current_balance || 0);
      let debit = 0;
      let credit = 0;

      // Determine debit/credit based on account type and balance
      if (account.account_type === 'asset' || account.account_type === 'expense') {
        if (balance > 0) {
          debit = balance;
        } else if (balance < 0) {
          credit = Math.abs(balance);
        }
      } else if (account.account_type === 'liability' || account.account_type === 'equity' || account.account_type === 'revenue') {
        if (balance > 0) {
          credit = balance;
        } else if (balance < 0) {
          debit = Math.abs(balance);
        }
      }

      // Add to totals
      totalDebit += debit;
      totalCredit += credit;

      return {
        ...account,
        debit,
        credit
      };
    }).filter(account => account.debit !== 0 || account.credit !== 0);

    return (
      <div>
        <h2 style={styles.reportTitle}>
          <span>📊</span> Trial Balance
        </h2>
        <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '16px' }}>
          As of {new Date(filters.as_of_date).toLocaleDateString('en-IN')}
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Account Code</th>
                <th style={styles.th}>Account Name</th>
                {!isMobile && <th style={styles.th}>Type</th>}
                <th style={{ ...styles.th, textAlign: 'right' }}>Debit (₹)</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {accountRows?.map(account => (
                <tr key={account.id}>
                  <td style={styles.td}><strong>{account.account_code}</strong></td>
                  <td style={styles.td}>{account.account_name}</td>
                  {!isMobile && (
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        background: colors.lightGray,
                        color: colors.textDark
                      }}>
                        {account.account_type}
                      </span>
                    </td>
                  )}
                  <td style={{ ...styles.td, textAlign: 'right', color: account.debit > 0 ? colors.success : colors.textLight }}>
                    {account.debit > 0 ? account.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', color: account.credit > 0 ? colors.danger : colors.textLight }}>
                    {account.credit > 0 ? account.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: colors.primary + '10' }}>
                <td colSpan={isMobile ? 2 : 3} style={{ ...styles.td, fontWeight: '700', fontSize: '15px' }}>
                  Total
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', fontSize: '15px', color: colors.success }}>
                  ₹{totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', fontSize: '15px', color: colors.danger }}>
                  ₹{totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Balance Check */}
        <div style={{
          marginTop: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: Math.abs(totalDebit - totalCredit) < 0.01 ? colors.success + '15' : colors.danger + '15',
          border: `1px solid ${Math.abs(totalDebit - totalCredit) < 0.01 ? colors.success : colors.danger}30`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '600', color: colors.textDark }}>
              {Math.abs(totalDebit - totalCredit) < 0.01 ? '✅ Trial Balance is Balanced' : '⚠️ Trial Balance has Difference'}
            </span>
            {Math.abs(totalDebit - totalCredit) >= 0.01 && (
              <span style={{ color: colors.danger, fontWeight: '600' }}>
                Difference: ₹{Math.abs(totalDebit - totalCredit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && activeReport !== 'gst-summary') {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading report...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.pageTitle}>Accounts Reports</h1>
            <p style={styles.pageSubtitle}>
              {isMobile ? 'Financial & GST reports' : 'GST summary, financial reports, and accounting analytics'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs - Only GST Report and Trial Balance */}
      <div style={styles.tabContainer}>
        <button
          style={styles.tab(activeReport === 'gst-summary')}
          onClick={() => setActiveReport('gst-summary')}
        >
          🧾 GST Report
        </button>
        <button
          style={styles.tab(activeReport === 'trial-balance')}
          onClick={() => setActiveReport('trial-balance')}
        >
          📊 Trial Balance
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filterCard}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', alignItems: 'end' }}>
          {activeReport === 'trial-balance' && (
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                As of Date
              </label>
              <input
                type="date"
                value={filters.as_of_date}
                onChange={(e) => setFilters({ ...filters, as_of_date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.mediumGray}`,
                  fontSize: '14px'
                }}
              />
            </div>
          )}
          {activeReport === 'gst-summary' && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.mediumGray}`,
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.mediumGray}`,
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <button
                  style={styles.button}
                  onClick={fetchReportData}
                >
                  🔄 Generate Report
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Report Content */}
      <div style={styles.contentCard}>
        {activeReport === 'gst-summary' && <GSTSummaryReport />}
        {activeReport === 'trial-balance' && <TrialBalanceReport />}
      </div>
    </AdminLayout>
  );
};

export default AccountsReports;
